import React, { useEffect, useState } from 'react';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '@styles/colors';
import Resumen from '@components/Resumen';
import { useCita } from '@providers/citaContext';

/** Rutas del flujo de agendamiento (misma lógica que el resumen lateral). */
export function isAgendaBookingPath(pathname) {
  const p = (pathname || '/').replace(/\/$/, '') || '/';
  return (
    p === '/home'
    || p === '/servicios'
    || p === '/ubicacion'
    || p === '/modalidad'
    || p === '/fechas'
    || p === '/confirmar'
    || p === '/final'
    || p.startsWith('/agenda/')
  );
}

const STEPS = [
  {
    key: 'ciudad',
    label: 'Ciudad',
    Icon: LocationCityIcon,
    done: (c) => Boolean(c?.ciudadNombre),
  },
  {
    key: 'servicio',
    label: 'Servicio',
    Icon: MedicalServicesIcon,
    done: (c) => Boolean(c?.especialidadNombre || c?.nombreServicio),
  },
  {
    key: 'sede',
    label: 'Sede',
    Icon: StorefrontIcon,
    done: (c) => Boolean(c?.sedeNombre),
  },
  {
    key: 'fecha',
    label: 'Fecha',
    Icon: CalendarMonthIcon,
    done: (c) => Boolean(c?.fecha),
  },
  {
    key: 'medico',
    label: 'Médico',
    Icon: PersonIcon,
    done: (c) => Boolean(c?.medicoNombre),
  },
  {
    key: 'hora',
    label: 'Horario',
    Icon: ScheduleIcon,
    done: (c) => Boolean(c?.horaSeleccionada),
  },
];

function resumenPropsFromCita(cita) {
  const c = cita && typeof cita === 'object' ? cita : {};
  return {
    ciudad: c.ciudadNombre || '',
    servicio: c.especialidadNombre || '',
    ubicacion: c.sedeNombre || '',
    fecha: c.fecha || '',
    medico: c.medicoNombre || '',
    hora: c.horaSeleccionada || '',
    nomServicio: c.nombreServicio || '',
    descripcion: c.descripcion || '',
  };
}

/**
 * Pie fijo en móvil/tablet durante el agendamiento: pasos con iconos y chulito
 * al completar cada dato (mismo criterio que Resumen). Tocar pasos o “Resumen” abre el modal del resumen.
 */
export default function AgendaMobileProgressFooter() {
  const { cita } = useCita();
  const [resumenOpen, setResumenOpen] = useState(false);

  useEffect(() => {
    if (!resumenOpen) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [resumenOpen]);

  const resumenProps = resumenPropsFromCita(cita);

  return (
    <>
      <div
        id="divCajaFooterAgenda"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 999,
          backgroundColor: colors.gray150,
          borderTop: `1px solid ${colors.borderLight}`,
          paddingTop: '0.35rem',
          paddingBottom: 'max(0.45rem, env(safe-area-inset-bottom, 0px))',
          paddingLeft: '0.35rem',
          paddingRight: '0.35rem',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem',
            maxWidth: '100%',
          }}
        >
          <div
            role="presentation"
            onClick={() => setResumenOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              justifyContent: 'space-evenly',
            }}
          >
            {STEPS.map((step, i) => {
              const StepIcon = step.Icon;
              const complete = step.done(cita);
              const prevComplete = i > 0 ? STEPS[i - 1].done(cita) : false;
              return (
                <React.Fragment key={step.key}>
                  {i > 0 && (
                    <div
                      aria-hidden
                      className={prevComplete ? 'agenda-progress-connector agenda-progress-connector--done' : 'agenda-progress-connector'}
                    />
                  )}
                  <button
                    type="button"
                    title={`${step.label}${complete ? ' — listo' : ''}. Ver resumen`}
                    aria-label={`${step.label}${complete ? ', completado' : ', pendiente'}. Abrir resumen de agendamiento`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setResumenOpen(true);
                    }}
                    style={{
                      position: 'relative',
                      border: 'none',
                      background: 'transparent',
                      padding: '0.15rem',
                      cursor: 'pointer',
                      flex: '0 0 auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <StepIcon
                      style={{
                        fontSize: '1.45rem',
                        color: complete ? colors.accent : colors.gray600,
                        transition: 'color 0.25s ease, transform 0.25s ease',
                      }}
                    />
                    {complete && (
                      <CheckCircleIcon
                        className="agenda-step-check-icon"
                        style={{
                          position: 'absolute',
                          right: -2,
                          bottom: -2,
                          fontSize: '0.95rem',
                          color: colors.primaryText,
                          backgroundColor: colors.white,
                          borderRadius: '50%',
                        }}
                      />
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setResumenOpen(true)}
          style={{
            display: 'block',
            width: '100%',
            marginTop: '0.2rem',
            border: 'none',
            background: 'transparent',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.72rem',
            fontWeight: 600,
            color: colors.primaryText,
            cursor: 'pointer',
            padding: '0.15rem',
          }}
        >
          Ver resumen de agendamiento
        </button>
      </div>

      {resumenOpen && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => setResumenOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Resumen de agendamiento"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(92vw, 380px)',
              maxHeight: '85vh',
              overflow: 'auto',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                aria-label="Cerrar resumen"
                onClick={() => setResumenOpen(false)}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  border: 'none',
                  background: colors.white,
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                }}
              >
                <CloseIcon style={{ color: colors.gray700 }} />
              </button>
              <Resumen {...resumenProps} variant="overlay" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
